import React, { useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { uploadImage } from "../services/user";
import { signUpUser, verifyOtp } from "../services/auth";
import { useNavigate } from "react-router-dom";

const SignupForm: React.FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [step, setStep] = useState<"signup" | "otp">("signup");
  const navigate = useNavigate();
  // OTP
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Handle profile photo preview
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  // ===== OTP Step =====
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 3) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== 4) {
      setOtpError("Please enter a valid 4-digit OTP.");
      return;
    }
    setOtpError("");
    setLoading(true);
    try {
      let otp = await verifyOtp({ id: userId, email: email, otp: code });
      if (!otp.success) {
        setOtpError('Invalid Otp');
      } else {
        navigate('/login')
      }

    } catch (err: any) {
      setOtpError(err.response?.data?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!name) newErrors.name = "Name is required";
    if (!email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email))
      newErrors.email = "Invalid email format";
    if (!mobile) newErrors.mobile = "Mobile number is required";
    else if (!/^\d{10}$/.test(mobile))
      newErrors.mobile = "Mobile must be 10 digits";
    if (!password) newErrors.password = "Password is required";
    else if (password.length < 6)
      newErrors.password = "Password must be at least 6 characters";
    if (password !== confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    if (!termsAccepted)
      newErrors.termsAccepted = "You must accept the terms and conditions";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      let response: any = {};
      const formData = new FormData();
      if (profilePhoto) {
        formData.append("file", profilePhoto);

        response = await uploadImage(formData);
        if (!response.success) {
          throw new Error(response.message);
        }
      }
      let userData = {
        name,
        email,
        phoneNumber: mobile,
        password,
        confirmPassword: password,
        profilePath: response.data,
      };
      let signupResPonse = await signUpUser(userData);
      if (!signupResPonse.success) {
        throw new Error(signupResPonse.message);
      } else {
        setUserId(signupResPonse.data._id);
        setStep('otp');

      }
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };
  // ===== UI Layout =====
  const renderForm = () => {
    switch (step) {
      case "signup":
        return (
          <>
            {/* Right Panel - Form */}
            <div className="p-1 overflow-y-auto">
              <h2 className="text-3xl font-semibold text-gray-800 mb-4">
                Create Account
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Profile Photo */}
                {apiError && (
                  <div className="bg-red-100 text-red-700 text-sm p-3 rounded-md text-center">
                    {apiError}
                  </div>
                )}
  <label className="block mb-1 text-gray-600">Profile Photo</label>
                <div className="relative group w-24 h-24">
              
                  <img src={photoPreview || '/default-profile.png'} className="w-24 h-24 rounded-full object-cover" />
                  <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer">
                    <span className="text-white text-sm">Change</span>
                    <input type="file" className="hidden" accept="image/*" onChange={handlePhotoChange} />
                  </label>
                </div>
                {/* <div>
                  
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:border file:rounded-lg file:border-gray-300 file:text-sm file:bg-white file:text-gray-700 hover:file:bg-gray-100"
                  />
                  {photoPreview && (
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-24 h-24 rounded-full mt-2 object-cover"
                    />
                  )}
                </div> */}

                {/* Name */}
                <InputField
                  label="Full Name"
                  value={name}
                  onChange={setName}
                  error={errors.name}
                  placeholder="Enter your full name"
                />

                {/* Mobile */}
                <InputField
                  label="Mobile Number"
                  value={mobile}
                  onChange={setMobile}
                  error={errors.mobile}
                  placeholder="10-digit mobile number"
                />

                {/* Email */}
                <InputField
                  label="Email"
                  value={email}
                  onChange={setEmail}
                  error={errors.email}
                  placeholder="Enter your email"
                />

                {/* Password with Eye */}
                <PasswordField
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  error={errors.password}
                  show={showPassword}
                  setShow={setShowPassword}
                />

                {/* Confirm Password with Eye */}
                <PasswordField
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  error={errors.confirmPassword}
                  show={showConfirmPassword}
                  setShow={setShowConfirmPassword}
                />

                {/* Terms and Conditions */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label className="ml-2 text-gray-700">
                    I accept the{" "}
                    <a href="#" className="text-green-600 hover:underline">
                      Terms and Conditions
                    </a>
                  </label>
                </div>
                {errors.termsAccepted && (
                  <p className="text-red-500 text-sm">{errors.termsAccepted}</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition duration-200"
                >
                  {loading ? "Creating Account..." : "Sign Up"}
                </button>
              </form>
              {/* Login Link */}
              <div className="text-center mt-3">
                <p className="text-sm">
                  Already have an account?{" "}
                  <a href="/login" className="text-green-600 hover:underline">
                    Login
                  </a>
                </p>
              </div>
            </div>
          </>
        );
      case "otp":
        return (
          <>
            <h2 className="text-3xl font-semibold text-gray-800 mb-6">
              OTP Verification
            </h2>
            <form onSubmit={handleOtpSubmit} className="space-y-6">
              {otpError && (
                <div className="bg-red-100 text-red-700 p-3 rounded">
                  {otpError}
                </div>
              )}
              <div className="flex justify-between space-x-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      if (el) inputRefs.current[index] = el;
                    }}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    className="w-14 h-14 text-center text-xl border border-gray-300 rounded-lg focus:ring-green-400"
                  />
                ))}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600"
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
              <div className="text-center mt-3">
                <p className="text-sm">
                  Already have an account?{' '}
                  <a href="/login" className="text-green-600 hover:underline">
                    Login
                  </a>
                </p>
              </div>
            </form>
          </>
        );
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white rounded-2xl shadow-lg flex max-w-4xl w-full">
        {/* Left Panel */}
        <div className="w-1/2 bg-gradient-to-br from-green-400 to-green-600 text-white flex flex-col items-center justify-center rounded-l-2xl p-8">
          <img
            src={require("../assets/logo.png")}
            alt="Logo"
            className="w-40 h-40 object-contain mb-4"
          />
          <h2 className="text-2xl font-bold">
            {step === "signup"
              ? "Welcome Aboard!"
              : "Verify OTP"}
          </h2>
          <p className="text-center mt-2">
            {step === "signup"
              ? "Let's get you started!"
              : "Enter the OTP sent to your email"}
          </p>
        </div>

        {/* Right Panel */}
        <div className="w-1/2 p-8">{renderForm()}</div>
      </div>
    </div>
  );
};

// Helper components for clean code

const InputField = ({ label, value, onChange, error, placeholder }: any) => (
  <div>
    <label className="block mb-1 text-gray-600">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-2 border ${error ? "border-red-500" : "border-gray-300"
        } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400`}
    />
    {error && <p className="text-red-500 text-sm">{error}</p>}
  </div>
);

const PasswordField = ({
  label,
  value,
  onChange,
  error,
  show,
  setShow,
}: any) => (
  <div className="relative">
    <label className="block mb-1 text-gray-600">{label}</label>
    <input
      type={show ? "text" : "password"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={label}
      className={`w-full px-4 py-2 border ${error ? "border-red-500" : "border-gray-300"
        } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400`}
    />
    <button
      type="button"
      onClick={() => setShow(!show)}
      className="absolute right-3 top-9 text-gray-500"
    >
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
    {error && <p className="text-red-500 text-sm">{error}</p>}
  </div>
);

export default SignupForm;
